import React from "react";
import { Box, Card, Chip, IconButton, InputAdornment, Stack, TextField, Tooltip, CircularProgress } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/Refresh";
import { PALETTE, StatusFilter } from "../types";

type Counts = { pending: number; approved: number; rejected: number; all: number };

const chipStyle = (active: boolean, tone: { bg: string; fg: string; accent: string }) => ({
  fontWeight: 800, fontSize: 11.5, letterSpacing: "0.02em",
  bgcolor: active ? tone.bg : "transparent",
  color: active ? tone.fg : PALETTE.slate,
  border: `1px solid ${active ? tone.accent : PALETTE.border}`,
  cursor: "pointer",
  "&:hover": { bgcolor: tone.bg },
});

const MasteryApprovalFilters: React.FC<{
  status: StatusFilter;
  onStatus: (s: StatusFilter) => void;
  search: string;
  onSearch: (v: string) => void;
  onRefresh: () => void;
  isFetching: boolean;
  counts?: Counts;
}> = ({ status, onStatus, search, onSearch, onRefresh, isFetching, counts }) => {
  return (
    <Card sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${PALETTE.border}`, boxShadow: "none" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} justifyContent="space-between">
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search student, program, target..."
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ maxWidth: { md: 360 }, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", gap: 0.75 }}>
          <Chip label={`Pending${counts ? ` (${counts.pending})` : ""}`} onClick={() => onStatus("pending")} sx={chipStyle(status === "pending", PALETTE.pending)} />
          <Chip label={`Approved${counts ? ` (${counts.approved})` : ""}`} onClick={() => onStatus("approved")} sx={chipStyle(status === "approved", PALETTE.approved)} />
          <Chip label={`Rejected${counts ? ` (${counts.rejected})` : ""}`} onClick={() => onStatus("rejected")} sx={chipStyle(status === "rejected", PALETTE.rejected)} />
          <Chip label={`All${counts ? ` (${counts.all})` : ""}`} onClick={() => onStatus("all")} sx={chipStyle(status === "all", { bg: PALETTE.info.bg, fg: PALETTE.navy, accent: PALETTE.navy2 })} />
          <Box sx={{ width: 4 }} />
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={isFetching} sx={{ border: `1px solid ${PALETTE.border}`, borderRadius: 2 }}>
                {isFetching ? <CircularProgress size={16} /> : <RefreshOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
};

export default MasteryApprovalFilters;
